import { useEffect } from "react";
import { Box, Container, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const CONTACT_EMAIL = "admin@chikitsalink.in";

export default function PrivacyPage() {
  useEffect(() => {
    const previous = document.title;
    document.title = "Privacy policy — ChikitsaLink";
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <Typography variant="overline" color="primary.dark" fontWeight={700}>
        Legal
      </Typography>
      <Typography variant="h3" sx={{ mt: 1, mb: 2 }}>
        Privacy policy
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1 }}>
        Last updated 27 October 2026. This page describes how ChikitsaLink
        handles personal information on{" "}
        <MuiLink href="https://chikitsalink.in" underline="hover">
          chikitsalink.in
        </MuiLink>{" "}
        and in the ChikitsaLink Android app.
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 5 }}>
        ChikitsaLink is an online consultation service for patients and
        doctors in India. We collect account details, health information you
        choose to share for a consultation, and payment references so we can
        book visits, run video calls, issue prescriptions, and send
        reminders. We do not sell personal information.
      </Typography>

      <Stack spacing={4}>
        <Section title="Accounts and sign-in">
          <P>
            Sign-in and registration run through our identity service
            (Keycloak) at auth.chikitsalink.in. When you create an account we
            store your name, email address, a hashed password, whether you
            joined as a patient or a doctor, and your role. You must verify
            your email before you can use the service. Email verification and
            password-reset messages are sent from noreply@chikitsalink.in.
          </P>
          <P>
            The website signs you in with a short-lived access token kept in
            the browser for the session. The Android app can also keep an
            encrypted refresh token on the device, unlocked with your
            fingerprint, face, or device PIN, so you can open the app again
            without typing your password. Signing out revokes that refresh
            token.
          </P>
        </Section>

        <Section title="Patient profile">
          <P>
            After you sign in you can save a profile: full name, date of
            birth, gender, blood group, address, mobile number, profile photo,
            and an emergency contact number. These fields are optional except
            your name. Only you, and the systems that deliver your care, use
            this profile. It is not shown on the public doctor directory.
          </P>
        </Section>

        <Section title="Doctor profile and verification">
          <P>
            Doctors add a public profile: name, photo, qualification, years of
            experience, bio, consultation fee, specializations, and weekly
            availability. To practise on ChikitsaLink a doctor also submits a
            medical-council registration number, council name, registration
            year, and documents (license, identity proof, degree, and any
            extra files). Those files are stored in our database and are
            reviewed by ChikitsaLink admins. Patients see a doctor only after
            approval.
          </P>
          <P>
            Approved profiles — including the bio, fee, ratings, availability,
            and registration number — are visible to anyone who browses the
            doctor directory. License files, identity proof, and degree
            documents stay with the doctor and our admins.
          </P>
        </Section>

        <Section title="Appointments, reports, and prescriptions">
          <P>
            When you book, we store the patient, the doctor, the slot, the
            fee, and the appointment status. You may add a note for the
            doctor and upload reports (the file, its name, type, size, and an
            optional caption). Reports stay on our servers and are available
            to you and the doctor for that appointment. The doctor may add a
            consultation remark.
          </P>
          <P>
            A prescription can include a diagnosis, medicines (name, dosage,
            frequency, duration, and instructions), a diagnostic referral, and
            remarks. We generate a verification code and a PDF. The PDF can
            be emailed to the patient when the consultation is completed.
            Anyone who has the verification link can open a public page that
            shows the patient name, doctor name, registration number,
            specialization, date, diagnosis, medicines, referral, and remarks,
            so a pharmacy can check that the prescription is genuine.
          </P>
          <P>
            Patients can leave a star rating and a written review after a
            consultation. Reviews appear on the doctor’s public profile.
          </P>
          <P>
            We keep an audit log of appointment status changes (who did what,
            and when) so admins can investigate cancellations, no-shows, and
            refunds.
          </P>
        </Section>

        <Section title="Payments and refunds">
          <P>
            Consultation fees are charged in Indian rupees through Razorpay
            (UPI, cards, net banking, or wallets). ChikitsaLink stores the
            amount, currency, appointment, Razorpay order id, and Razorpay
            payment id. Card numbers, UPI handles, and bank details are
            entered on Razorpay’s checkout and are not saved in our database.
          </P>
          <P>
            If a paid appointment is cancelled, we can raise a refund. Admins
            approve or decline it, and an approved refund is sent back through
            Razorpay. We store the refund status and, when Razorpay issues
            one, the refund id.
          </P>
        </Section>

        <Section title="Video and messages">
          <P>
            Video consultations use Daily.co. Our servers create a private
            room and a short-lived meeting token for the doctor and the
            patient. The browser or the Android app joins that room with your
            camera and microphone. ChikitsaLink does not record the call and
            does not store the video or audio.
          </P>
          <P>
            We email you about bookings, confirmations, cancellations,
            reschedules, completed consultations, refunds, and doctor
            verification. The Android app can also register a Firebase Cloud
            Messaging token so we can send the same kinds of alerts to the
            device. Notification text on the lock screen does not include a
            diagnosis, medicine name, or report name.
          </P>
        </Section>

        <Section title="Who else processes information">
          <P>
            We use these providers only to run the product. Each one receives
            the information it needs for its job:
          </P>
          <Bullet>
            Keycloak, hosted by us, for accounts, email verification, and
            sign-in.
          </Bullet>
          <Bullet>
            Razorpay, for taking payments and issuing refunds.
          </Bullet>
          <Bullet>Daily.co, for the live video room.</Bullet>
          <Bullet>
            ZeptoMail (or Zoho Mail, if configured), for transactional email.
          </Bullet>
          <Bullet>
            Firebase Cloud Messaging, for push notifications on Android.
          </Bullet>
          <Bullet>
            Firebase Crashlytics, for Android crash reports: the app version,
            phone model, Android version, and the technical stack trace of the
            crash. Error text that could contain your details is removed
            before sending. You can turn this off under Settings → Send crash
            reports.
          </Bullet>
          <Bullet>
            Google Play Integrity, which tells us once per sign-in whether the
            phone appears rooted or modified so the app can warn you. It
            never blocks you from using the service.
          </Bullet>
          <Bullet>
            Google Fonts, which the website loads so pages use the Plus
            Jakarta Sans and Inter typefaces. Your browser contacts Google
            when it fetches those font files.
          </Bullet>
          <P>
            The treating doctor sees the appointment note, uploaded reports,
            and prescription for visits you book with them. ChikitsaLink
            admins can see doctor verification documents, appointment records,
            and refunds so they can approve doctors and handle payments.
            Prescription details are also visible to anyone who opens a valid
            verification link.
          </P>
        </Section>

        <Section title="Cookies and the Android app">
          <P>
            The website does not use advertising or analytics cookies.
            Sign-in uses a session on auth.chikitsalink.in. Razorpay and
            Daily may set their own cookies or local storage while you pay
            or join a video consultation.
          </P>
          <P>
            The Android app asks for the camera and microphone during a video
            call, and for photos or files when you upload a report or a
            doctor document. It can use the device credential (biometric or
            PIN) to unlock the saved sign-in. Push notifications are optional
            in the sense that the app registers a device token only when
            Firebase is configured; you can turn notifications off in Android
            settings.
          </P>
        </Section>

        <Section title="How long we keep information">
          <P>
            We keep account, consultation, prescription, payment, and refund
            records for as long as your account is open and for as long as we
            need them to provide care, resolve disputes, and meet tax and
            medical-record duties. Uploaded reports remain with the
            appointment. Closing the app or signing out does not delete the
            record of a consultation you already had.
          </P>
          <P>
            You can ask us to delete your account and personal data at{" "}
            <MuiLink component={RouterLink} to="/delete-account" underline="hover">
              chikitsalink.in/delete-account
            </MuiLink>
            , or from Settings in the Android app. You do not need to be
            signed in. We delete the account, profile, notes, uploaded files,
            and device tokens within 30 days. A prescription, payment, refund,
            or other legal duty can mean we keep that specific record for the
            required period. You can also email <ContactLink />.
          </P>
        </Section>

        <Section title="Your choices">
          <P>
            You can update your patient profile from the website or the app,
            remove an uploaded report while the appointment is still pending
            or confirmed, and correct doctor profile details before or after
            verification (document changes go through review again where the
            product requires it). You can withhold optional profile fields.
            You can stop using the service, and you can email <ContactLink />{" "}
            to ask for a copy of the personal information we hold, to correct
            it, or to raise a grievance about how we handle it.
          </P>
          <P>
            Health information you type into a note, upload as a report, or
            receive in a prescription is used to deliver that consultation. If
            you do not want a particular report shared with the doctor, do
            not upload it.
          </P>
        </Section>

        <Section title="Security">
          <P>
            The site is served over HTTPS. The API checks a signed sign-in
            token before it returns your appointments, reports, or payments.
            Doctor documents and consultation files are not part of the public
            doctor directory. Android refresh tokens are encrypted with a key
            stored in the device keystore. So you can read your appointments,
            prescriptions, payments, and notifications without a connection,
            the app keeps a copy of them in an encrypted database on the phone,
            which is erased when you sign out.
          </P>
        </Section>

        <Section title="Children">
          <P>
            ChikitsaLink is meant for adults who can book a medical
            consultation. We do not knowingly create accounts for children.
            If you believe a child has given us personal information, email{" "}
            <ContactLink /> and we will delete the account.
          </P>
        </Section>

        <Section title="Changes and contact">
          <P>
            When this policy changes, we update the date at the top of this
            page. Continued use of the website or the app after that date
            means the updated policy applies to new use of the service.
          </P>
          <P>
            For privacy questions, access requests, corrections, deletion
            requests, or grievances, email <ContactLink />. This service is
            not for emergencies. In a medical emergency, dial 112.
          </P>
          <P>
            Related pages:{" "}
            <MuiLink component={RouterLink} to="/how-it-works" underline="hover">
              How it works
            </MuiLink>
            .
          </P>
        </Section>
      </Stack>
    </Container>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box component="section">
      <Typography variant="h5" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <Stack spacing={1.5}>{children}</Stack>
    </Box>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <Typography color="text.secondary">{children}</Typography>;
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <Typography component="li" color="text.secondary" sx={{ ml: 2.5 }}>
      {children}
    </Typography>
  );
}

function ContactLink() {
  return (
    <MuiLink href={`mailto:${CONTACT_EMAIL}`} underline="hover">
      {CONTACT_EMAIL}
    </MuiLink>
  );
}
